import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { SearchInput, Text } from '@figma/fpl-components';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/Common/EmptyState/EmptyState';
import { I18N_KEYS } from '@/language/keys';
import bannerArt from './assets/banner-art.webp';
import bannerLogo from './assets/banner-logo.svg';
import { CatalogRow } from './CatalogRow';
import { ModelRow } from './ModelRow';
import { filterCatalog } from './filterCatalog';
import { GeneratorEntry } from './GeneratorEntry';
import { toolkitLayout } from './toolkit.stylex';
import type { ToolkitCatalog } from '@/types/toolkit.types';

const styles = stylex.create({
  // Undoes the dashboard's side padding, so the banner and the carousels can reach the column's edges.
  // It must not clip or scroll: the dashboard's main area scrolls, and the sticky header follows it.
  frame: {
    display: 'flex',
    flexDirection: 'column',
    marginInline: toolkitLayout.dashboardPaddingUndo,
  },
  content: {
    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '48px',
    paddingInline: {
      default: toolkitLayout.dashboardPadding,
      '@media (max-width: 600px)': toolkitLayout.narrowPadding,
    },
  },
  // The banner looks the same in both color modes, so its words are white on its own picture.
  banner: {
    alignItems: 'center',
    color: 'white',
    display: 'flex',
    gap: spacer.spacer4,
    height: '129px',
    isolation: 'isolate',
    marginInline: {
      default: toolkitLayout.dashboardPaddingUndo,
      '@media (max-width: 600px)': toolkitLayout.narrowPaddingUndo,
    },
    overflow: 'hidden',
    paddingInline: {
      default: toolkitLayout.dashboardPadding,
      '@media (max-width: 600px)': toolkitLayout.narrowPadding,
    },
    position: 'relative',
  },
  bannerArt: {
    height: '100%',
    inset: 0,
    objectFit: 'cover',
    position: 'absolute',
    width: '100%',
    zIndex: -1,
  },
  bannerLogo: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: '48px',
    justifyContent: 'center',
    width: '48px',
  },
  bannerWords: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  bannerSubtitle: {
    color: 'rgb(255 255 255 / 0.7)',
  },
  text: {
    margin: 0,
  },
  // Marks the top of the page: once it scrolls out of view, the header turns compact.
  topMarker: {
    height: '1px',
    marginBottom: '-1px',
  },
  header: {
    alignItems: 'center',
    backgroundColor: color.bg,
    columnGap: '64px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginInline: {
      default: toolkitLayout.dashboardPaddingUndo,
      '@media (max-width: 600px)': toolkitLayout.narrowPaddingUndo,
    },
    paddingBottom: spacer.spacer6,
    paddingInline: {
      default: toolkitLayout.dashboardPadding,
      '@media (max-width: 600px)': toolkitLayout.narrowPadding,
    },
    paddingTop: spacer.spacer5,
    position: 'sticky',
    rowGap: spacer.spacer3,
    top: 0,
    transition: {
      default: 'padding 0.2s ease-out',
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
    zIndex: 2,
  },
  headerCompact: {
    paddingBottom: spacer.spacer2,
    paddingTop: spacer.spacer2,
  },
  search: {
    width: {
      default: '240px',
      '@media (max-width: 600px)': '144px',
    },
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: toolkitLayout.sectionGap,
  },
});

/**
 * Becomes true once the element has scrolled out of the window. The dashboard's scrolling area starts at
 * the top of the window, so leaving the window is leaving that area.
 */
function useScrolledPast(marker: RefObject<HTMLElement | null>) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!marker.current) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsScrolled(!entry.isIntersecting));
    observer.observe(marker.current);
    return () => observer.disconnect();
  }, [marker]);

  return isScrolled;
}

interface ToolsCatalogProps {
  catalog: ToolkitCatalog;
}

/**
 * The Toolkit page: a banner, the title and search, the ways into the generator, and one row per category.
 */
export function ToolsCatalog({ catalog }: ToolsCatalogProps) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const topMarkerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const isScrolled = useScrolledPast(topMarkerRef);
  const visibleRows = useMemo(() => filterCatalog(catalog.rows, searchText), [catalog.rows, searchText]);
  const isSearching = searchText.trim() !== '';
  const makers = useMemo(
    () => new Set(catalog.rows.flatMap((row) => (row.kind === 'models' ? row.cards.map((card) => card.maker) : []))),
    [catalog.rows],
  );

  // Cmd+K (Ctrl+K) puts the cursor in the search box. It only means that in the workflow editor
  // otherwise, which is never open at the same time as the Toolkit.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div {...stylex.props(styles.frame)}>
      <div {...stylex.props(styles.content)}>
        <div {...stylex.props(styles.banner)}>
          <img {...stylex.props(styles.bannerArt)} src={bannerArt} alt="" />
          <span {...stylex.props(styles.bannerLogo)} aria-hidden>
            <img src={bannerLogo} alt="" />
          </span>
          <div {...stylex.props(styles.bannerWords)}>
            <Text.h2 {...stylex.props(styles.text)} variant="heading-medium" color="inherit">
              {t(I18N_KEYS.TOOLKIT.BANNER_TITLE)}
            </Text.h2>
            <Text.p {...stylex.props(styles.text, styles.bannerSubtitle)} variant="body-large" color="inherit">
              {t(I18N_KEYS.TOOLKIT.BANNER_SUBTITLE)}
            </Text.p>
          </div>
        </div>
        <div ref={topMarkerRef} {...stylex.props(styles.topMarker)} />
        <div {...stylex.props(styles.header, isScrolled && styles.headerCompact)}>
          <Text.h1 {...stylex.props(styles.text)} variant="heading-small" color="default">
            {t(I18N_KEYS.TOOLKIT.TITLE)}
          </Text.h1>
          <div {...stylex.props(styles.search)}>
            <SearchInput
              ref={searchRef}
              aria-label={t(I18N_KEYS.TOOLKIT.SEARCH_PLACEHOLDER)}
              placeholder={t(I18N_KEYS.TOOLKIT.SEARCH_PLACEHOLDER)}
              value={searchText}
              onChange={setSearchText}
            />
          </div>
        </div>
        <div {...stylex.props(styles.sections)}>
          {isSearching ? null : <GeneratorEntry makers={makers} />}
          {visibleRows.length === 0 ? (
            <EmptyState
              title={t(I18N_KEYS.TOOLKIT.NO_RESULTS_TITLE)}
              description={t(I18N_KEYS.TOOLKIT.NO_RESULTS, { searchTerm: searchText.trim() })}
              actions={[{ label: t(I18N_KEYS.TOOLKIT.EXPLORE_ALL), onClick: () => setSearchText('') }]}
              sx={{ paddingTop: '104px' }}
            />
          ) : (
            visibleRows.map((row) =>
              row.kind === 'models' ? (
                <ModelRow key={`${row.id}:${searchText}`} title={row.title} subtitle={row.subtitle} cards={row.cards} />
              ) : (
                // Keyed by the search text too, so a row starts again closed and from its first match.
                <CatalogRow
                  key={`${row.id}:${searchText}`}
                  title={row.title}
                  subtitle={row.subtitle}
                  cards={row.cards}
                />
              ),
            )
          )}
        </div>
      </div>
    </div>
  );
}
